import { useCallback } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowUp, ArrowDown, X, GripVertical } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import type { SortDefinition } from '../../core/types/workspaceTypes';
import { normalizeColumnName } from '../../common/utils/normalizeColumnName';

type SortChipProps = {
  sort: SortDefinition;
  index: number;
  id: string;
  onToggleDirection: () => void;
  onRemove: () => void;
};

/**
 * SortChip displays a single sort condition with direction toggle and drag handle
 */
export function SortChip({
  sort,
  index,
  id,
  onToggleDirection,
  onRemove,
}: SortChipProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleDirection();
    },
    [onToggleDirection]
  );

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onRemove();
    },
    [onRemove]
  );

  const isAscending = sort.direction === 'asc';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-1 rounded-md border border-border bg-muted/50 px-2 py-1 ${
        isDragging ? 'opacity-50 shadow-lg z-50' : ''
      }`}
      data-testid={`sort-chip-${normalizeColumnName(sort.field)}`}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-0 -ml-1 text-muted-foreground hover:text-foreground touch-none"
        title="Drag to reorder"
        data-testid={`sort-chip-drag-handle-${normalizeColumnName(sort.field)}`}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>

      {/* Sort number indicator */}
      <span className="text-[10px] text-muted-foreground font-medium min-w-[16px]">
        {index + 1}.
      </span>

      {/* Field name */}
      <span className="text-xs font-medium text-foreground shrink-0 select-text">
        {sort.field}
      </span>

      {/* Direction toggle button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleToggle}
        className="h-5 px-1 text-xs gap-0.5 hover:bg-accent"
        title={`Click to change to ${isAscending ? 'descending' : 'ascending'}`}
      >
        {isAscending ? (
          <>
            <ArrowUp className="h-3 w-3" />
            <span>Asc</span>
          </>
        ) : (
          <>
            <ArrowDown className="h-3 w-3" />
            <span>Desc</span>
          </>
        )}
      </Button>

      {/* Remove button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleRemove}
        className="h-5 w-5 p-0 hover:bg-destructive/20"
        title="Remove sort"
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
}
